import React from 'react';
import Modal from 'src/shared/components/modal/Modal';
import { PrimaryButton } from 'src/shared/components/button/Button';
import { formatRelativeDate, formatStandardDate } from 'src/shared/utils/dateHelpers';

export type Announcement = {
    id: number;
    title: string;
    content: string;
    publishedAt: string;
    imageUrl?: string;
};

type AnnouncementsModalProps = {
    announcements: Announcement[];
    onClose: () => void;
    isOpen: boolean;
};

const AnnouncementsModal = ({ announcements, onClose, isOpen }: AnnouncementsModalProps) => {
    if (!isOpen || announcements.length === 0) {
        return null;
    }

    return (
        <Modal
            onClose={onClose}
            dismissOnClickOutside={true}
            classNames={{ body: 'w-[900px] max-w-[95vw] max-h-[85vh] p-0 overflow-hidden' }}
        >
            <div className='flex h-full max-h-[85vh] min-h-0'>
                {/* Contents Menu */}
                <div className='w-64 shrink-0 border-r border-grey-20 bg-grey-0 p-4 overflow-y-auto'>
                    <h3 className='text-lg font-semibold text-black mb-4'>Contents</h3>
                    <nav className='space-y-2'>
                        {announcements.map((announcement, index) => (
                            <button
                                key={announcement.id}
                                onClick={() => {
                                    const element = document.getElementById(`announcement-${announcement.id}`);
                                    element?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className='w-full text-left p-3 rounded-lg hover:bg-grey-20 transition-colors group relative'
                            >
                                {index === 0 && (
                                    <span className='absolute -top-0.5 -right-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-white z-10'>
                                        New
                                    </span>
                                )}
                                <div className='flex-1 min-w-0'>
                                    <h4 className='text-sm font-medium text-black truncate group-hover:text-primary'>
                                        {announcement.title}
                                    </h4>
                                    <p className='text-xs text-grey-60 mt-1'>{formatRelativeDate(announcement.publishedAt)}</p>
                                </div>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Main Content */}
                <div className='flex-1 flex flex-col min-w-0'>
                    <div className='px-6 py-6 pb-4 border-b border-grey-20 bg-grey-0 shrink-0'>
                        <h2 className='text-2xl font-semibold text-black mb-2'>What&apos;s new?</h2>
                        <p className='text-sm text-grey-80'>Here are the latest updates from the NatureMetrics team</p>
                    </div>

                    <div className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6'>
                        <div className='space-y-8 min-w-0'>
                            {announcements.map((announcement, index) => (
                                <div
                                    key={announcement.id}
                                    id={`announcement-${announcement.id}`}
                                    className='relative p-6 rounded-lg border border-grey-20 bg-white min-w-0'
                                >
                                    {index === 0 && (
                                        <div className='absolute -top-3 left-6'>
                                            <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-white'>
                                                New
                                            </span>
                                        </div>
                                    )}

                                    <div className='flex justify-between items-start gap-4 mb-4 min-w-0'>
                                        <h3 className='text-xl font-semibold text-black flex-1 min-w-0 break-words'>
                                            {announcement.title}
                                        </h3>
                                        <span className='text-xs text-grey-60 whitespace-nowrap mt-1'>
                                            {formatStandardDate(announcement.publishedAt)}
                                        </span>
                                    </div>

                                    {announcement.imageUrl && (
                                        <div className='mb-4 rounded-lg overflow-hidden'>
                                            <img
                                                src={announcement.imageUrl}
                                                alt={announcement.title}
                                                className='w-full h-auto max-h-[250px] object-cover block'
                                            />
                                        </div>
                                    )}

                                    <div
                                        className='text-sm leading-relaxed text-grey-80 break-words [&>p]:mb-3 [&>p:last-child]:mb-0 [&_a]:text-link-blue [&_a]:no-underline hover:[&_a]:underline [&>ul]:my-3 [&>ol]:my-3 [&>ul]:pl-5 [&>ol]:pl-5 [&>li]:mb-1 [&>li]:break-words [&>strong]:font-semibold [&>em]:italic'
                                        dangerouslySetInnerHTML={{ __html: announcement.content }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='px-6 py-4 pb-6 border-t border-grey-20 bg-grey-0 flex justify-end shrink-0'>
                        <PrimaryButton onClick={onClose} className='min-w-[100px]'>
                            Got it
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AnnouncementsModal;
